import { createContext, useCallback, useContext, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState({
    title: "Are you sure?",
    description: "",
    confirmText: "Confirm",
    cancelText: "Cancel",
    destructive: false,
  });
  const resolver = useRef(null);

  const confirm = useCallback((options = {}) => {
    setOpts((o) => ({ ...o, ...options }));
    setOpen(true);
    return new Promise((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const handle = (result) => {
    setOpen(false);
    if (resolver.current) {
      resolver.current(result);
      resolver.current = null;
    }
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={open} onOpenChange={(v) => !v && handle(false)}>
        <AlertDialogContent
          className="bg-white border-[#DDE5EC] text-[#011836]"
          data-testid="confirm-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl text-[#011836]">
              {opts.title}
            </AlertDialogTitle>
            {opts.description && (
              <AlertDialogDescription className="text-[#5B6B7C]">
                {opts.description}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel
              onClick={() => handle(false)}
              className="border-[#C4D0DB] bg-transparent text-[#011836] hover:bg-[#EDF1F5] hover:text-[#011836]"
              data-testid="confirm-cancel-btn"
            >
              {opts.cancelText}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handle(true)}
              className={
                opts.destructive
                  ? "bg-[#EF4444] hover:bg-[#dc2626] text-white"
                  : "bg-[#0088CC] hover:bg-[#006DA6] text-white"
              }
              data-testid="confirm-ok-btn"
            >
              {opts.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used inside ConfirmProvider");
  return ctx;
}
